import React from 'react';
import { X } from 'lucide-react';
import { getVerticalBranding, type VerticalType } from '../config/vertical';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  vertical?: VerticalType;
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose, vertical = 'vineyard' }) => {
  if (!isOpen) return null;
  const { brandName } = getVerticalBranding(vertical);
  const brandUpper = brandName.toUpperCase();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-vineyard-white rounded shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col transform transition-all animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="flex items-center justify-between p-5 sm:p-6 border-b-2 border-wood-300 sticky top-0 bg-gradient-to-r from-vineyard-white via-vineyard-light to-vineyard-white z-10 shadow">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-wood-900 tracking-tighter">
              Terms and Conditions
            </h1>
            <p className="text-xs text-metal-600 mt-1.5">
              Last Updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-metal-500 hover:text-metal-700 hover:bg-metal-100 rounded-full p-1.5 transition-all duration-200 ml-4"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-5 sm:p-6 lg:p-7 bg-gradient-to-b from-vineyard-white to-vineyard-light">
          <div className="max-w-none text-wood-800 space-y-5 text-xs sm:text-sm leading-relaxed">
            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">1</span>
                Acceptance of Terms
              </h2>
              <p className="text-wood-800 leading-relaxed">
                By accessing or using {brandName} ("the Service"), you agree to be bound by these Terms and Conditions 
                ("Terms"). If you do not agree to these Terms, you may not access or use the Service. These Terms 
                constitute a legally binding agreement between you ("User" or "you") and {brandName}, with its 
                principal place of business in Dallas, Texas ({brandName}, "we," "us," or "our").
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">2</span>
                Trial Period and Subscription
              </h2>
              <div className="space-y-2.5">
                <p>
                  <strong className="text-wood-900">2.1 Trial Period:</strong> {brandName} may offer a trial period during which you can access 
                  certain features of the Service at no charge. The duration and scope of the trial period are 
                  determined by {brandName} and may vary.
                </p>
                <p>
                  <strong className="text-wood-900">2.2 Subscription Required After Trial:</strong> Upon expiration of the trial period, 
                  you must subscribe to a paid plan to continue accessing the Service. If you do not subscribe, 
                  your access to the Service will be limited or terminated. You acknowledge and agree that you 
                  understand this requirement and that continued use of the Service after the trial period requires 
                  a paid subscription.
                </p>
                <p>
                  <strong className="text-wood-900">2.3 Subscription Terms:</strong> Subscription fees, billing cycles, and payment terms 
                  are set forth in our subscription plans and may be updated from time to time. By subscribing, 
                  you agree to pay all applicable fees as described in your selected plan. Subscriptions automatically 
                  renew at the end of each billing cycle unless cancelled prior to the renewal date.
                </p>
                <p>
                  <strong className="text-wood-900">2.4 Cancellation and Refunds:</strong> You may cancel your subscription at any time 
                  through your account settings or by contacting customer support. Upon cancellation, you will 
                  receive a refund for the last 30-day billing period. Refunds will be processed within 10 business 
                  days to the original payment method. After the refund period, you will retain access to the Service 
                  until the end of your current billing cycle, but your subscription will not renew. No refunds will 
                  be provided for partial billing periods beyond the 30-day refund window.
                </p>
                <p>
                  <strong className="text-wood-900">2.5 Payment Authorization:</strong> By providing payment information, you authorize {brandName} 
                  to charge the applicable fees to your payment method. You are responsible for maintaining valid 
                  payment information. Failure to pay may result in suspension or termination of your account.
                </p>
              </div>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">3</span>
                Data Privacy and Usage
              </h2>
              <div className="space-y-2.5">
                <p>
                  <strong className="text-wood-900">3.1 Data Collection:</strong> {brandName} collects and processes data that you provide 
                  through the Service, including but not limited to vineyard information, block data, harvest records, 
                  and other operational data.
                </p>
                <p>
                  <strong className="text-wood-900">3.2 Data Privacy:</strong> We take reasonable measures to protect your data and maintain 
                  its privacy. However, you acknowledge that no method of transmission over the Internet or method 
                  of electronic storage is 100% secure.
                </p>
                <p>
                  <strong className="text-wood-900">3.3 Data Ownership and Usage Rights:</strong> You retain ownership of all data you input 
                  into the Service. By using the Service, you grant {brandName} a non-exclusive, worldwide, royalty-free 
                  license to use, store, process, and analyze your data solely for the purpose of providing and 
                  improving the Service. {brandName} may aggregate and anonymize your data for analytics and service 
                  improvement purposes. "Anonymized data" means data that has been processed to remove all personally 
                  identifiable information and specific vineyard identifiers such that the data cannot reasonably be 
                  traced back to you or your specific operations. {brandName} may use such anonymized, aggregated data 
                  for business intelligence, benchmarking, and industry research purposes. Individual customer data 
                  will never be sold or shared with third parties without your explicit consent, except as required 
                  by law or as necessary to provide the Service (e.g., payment processors, cloud hosting providers).
                </p>
                <p>
                  <strong className="text-wood-900">3.4 Personal Information:</strong> Your personal information will be handled in accordance 
                  with our Privacy Policy. However, you acknowledge that {brandName} may use your data as described 
                  in Section 3.3 above.
                </p>
              </div>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">4</span>
                User Responsibilities
              </h2>
              <div className="space-y-2.5">
                <p>
                  <strong className="text-wood-900">4.1 Account Security:</strong> You are responsible for maintaining the confidentiality 
                  of your account credentials and for all activities that occur under your account.
                </p>
                <p>
                  <strong className="text-wood-900">4.2 Acceptable Use:</strong> You agree to use the Service only for lawful purposes and 
                  in accordance with these Terms. You agree not to:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 mt-2 text-wood-800">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe upon the rights of others</li>
                  <li>Transmit any harmful, malicious, or illegal content</li>
                  <li>Attempt to gain unauthorized access to the Service</li>
                  <li>Interfere with or disrupt the Service or servers</li>
                </ul>
                <p>
                  <strong className="text-wood-900">4.3 Data Accuracy:</strong> You are responsible for the accuracy and completeness of 
                  data you input into the Service.
                </p>
              </div>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">5</span>
                Service Availability and Modifications
              </h2>
              <p>
                {brandName} reserves the right to modify, suspend, or discontinue the Service or any part thereof 
                at any time, with or without notice. {brandName} shall not be liable to you or any third party 
                for any modification, suspension, or discontinuance of the Service.
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">6</span>
                Intellectual Property
              </h2>
              <p>
                The Service, including its original content, features, and functionality, is owned by {brandName} 
                and is protected by international copyright, trademark, patent, trade secret, and other intellectual 
                property laws. You may not copy, modify, distribute, sell, or lease any part of the Service.
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">7</span>
                Limitation of Liability
              </h2>
              <div className="space-y-2.5">
                <p>
                  <strong className="text-wood-900">7.1 Disclaimer:</strong> THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT 
                  WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES 
                  OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                </p>
                <p>
                  <strong className="text-wood-900">7.2 Limitation:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, {brandUpper} SHALL NOT BE 
                  LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF 
                  PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, 
                  OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.
                </p>
                <p>
                  <strong className="text-wood-900">7.3 Maximum Liability:</strong> IN NO EVENT SHALL {brandUpper}&apos;S TOTAL LIABILITY TO YOU 
                  EXCEED THE AMOUNT YOU HAVE PAID TO {brandUpper} IN THE TWELVE (12) MONTHS PRIOR TO THE CLAIM.
                </p>
              </div>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">8</span>
                Termination
              </h2>
              <div className="space-y-2.5">
                <p>
                  <strong className="text-wood-900">8.1 By You:</strong> You may terminate your account at any time by contacting {brandName} 
                  or using account deletion features in the Service.
                </p>
                <p>
                  <strong className="text-wood-900">8.2 By {brandName}:</strong> {brandName} may terminate or suspend your account and access 
                  to the Service immediately, without prior notice, for any reason, including but not limited to 
                  breach of these Terms, non-payment, or violation of applicable laws.
                </p>
                <p>
                  <strong className="text-wood-900">8.3 Effect of Termination:</strong> Upon termination, your right to use the Service 
                  will immediately cease. {brandName} may delete your account and data, subject to applicable legal 
                  requirements.
                </p>
              </div>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">9</span>
                Changes to Terms
              </h2>
              <p>
                {brandName} reserves the right to modify these Terms at any time. Material changes will be notified 
                to you via email or through the Service. Your continued use of the Service after such modifications 
                constitutes acceptance of the updated Terms. If you do not agree to the modified Terms, you must 
                discontinue use of the Service.
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">10</span>
                Governing Law and Dispute Resolution
              </h2>
              <div className="space-y-2.5">
                <p>
                  <strong className="text-wood-900">10.1 Governing Law:</strong> These Terms shall be governed by and construed in accordance 
                  with the laws of the State of Texas, United States, without regard to its conflict of law provisions. 
                  The United Nations Convention on Contracts for the International Sale of Goods shall not apply.
                </p>
                <p>
                  <strong className="text-wood-900">10.2 Informal Resolution:</strong> Before initiating any formal dispute resolution, you 
                  agree to contact us at WorkRefined@gmail.com to attempt to resolve the dispute informally. We will attempt 
                  to resolve disputes in good faith for a period of 60 days.
                </p>
                <p>
                  <strong className="text-wood-900">10.3 Binding Arbitration:</strong> If informal resolution fails, any dispute, claim, or 
                  controversy arising out of or relating to these Terms or the Service shall be resolved by binding 
                  arbitration administered by the American Arbitration Association ("AAA") in accordance with its 
                  Commercial Arbitration Rules. The arbitration shall be conducted in Dallas County, Texas, or 
                  remotely via videoconference at the arbitrator's discretion. The arbitrator's decision shall be 
                  final and binding, and judgment may be entered in any court of competent jurisdiction.
                </p>
                <p>
                  <strong className="text-wood-900">10.4 Arbitration Costs:</strong> Each party shall bear its own attorney's fees and costs. 
                  The arbitrator's fees and AAA administrative fees shall be split equally between the parties, 
                  provided that {brandName} will pay your share of such fees if they exceed the filing fees you would 
                  have incurred in court.
                </p>
                <p>
                  <strong className="text-wood-900">10.5 Class Action Waiver:</strong> YOU AND {brandUpper} AGREE THAT EACH PARTY MAY BRING CLAIMS 
                  AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY AND NOT AS A CLASS MEMBER OR REPRESENTATIVE IN ANY 
                  CLASS, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. The arbitrator may not consolidate more than one 
                  party's claims and may not preside over any form of representative or class proceeding.
                </p>
                <p>
                  <strong className="text-wood-900">10.6 Opt-Out Right:</strong> You may opt out of this arbitration agreement by sending 
                  written notice to {brandName} at WorkRefined@gmail.com within 30 days of first accepting these Terms. 
                  Your notice must include your name, email address, and a clear statement that you wish to opt out 
                  of the arbitration agreement. If you opt out, all other terms of this agreement will still apply, 
                  but disputes will be resolved in the state or federal courts located in Dallas County, Texas.
                </p>
                <p>
                  <strong className="text-wood-900">10.7 Exceptions to Arbitration:</strong> Either party may seek injunctive or other equitable 
                  relief in any court of competent jurisdiction to prevent the actual or threatened infringement, 
                  misappropriation, or violation of intellectual property rights. Small claims court actions are also 
                  exempt from arbitration.
                </p>
                <p>
                  <strong className="text-wood-900">10.8 Venue:</strong> If arbitration does not apply (due to opt-out or exception), you agree 
                  to submit to the personal and exclusive jurisdiction of the state and federal courts located in 
                  Dallas County, Texas.
                </p>
              </div>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">11</span>
                Entire Agreement
              </h2>
              <p>
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and 
                {brandName} regarding the Service and supersede all prior agreements and understandings.
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">12</span>
                Indemnification
              </h2>
              <p>
                You agree to indemnify, defend, and hold harmless {brandName}, its officers, directors, employees, 
                agents, affiliates, and partners from and against any and all claims, liabilities, damages, losses, 
                costs, expenses, or fees (including reasonable attorneys' fees) arising from: (a) your use or misuse 
                of the Service; (b) your violation of these Terms; (c) your violation of any rights of another party, 
                including any users; (d) your violation of any applicable laws, rules, or regulations; or (e) any 
                content or data you submit, post, or transmit through the Service. {brandName} reserves the right to 
                assume the exclusive defense and control of any matter subject to indemnification by you, in which 
                event you will cooperate with {brandName} in asserting any available defenses.
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">13</span>
                Force Majeure
              </h2>
              <p>
                {brandName} shall not be liable for any failure or delay in performance of its obligations under these 
                Terms due to causes beyond its reasonable control, including but not limited to acts of God, natural 
                disasters, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, 
                accidents, pandemics, epidemics, strikes, or shortages of transportation, facilities, fuel, energy, 
                labor, or materials. During any such event, {brandName}&apos;s performance obligations shall be suspended 
                for the duration of the force majeure event.
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">14</span>
                Export Compliance
              </h2>
              <p>
                You agree to comply with all applicable export and import control laws and regulations in your use 
                of the Service. You represent and warrant that you are not located in, under the control of, or a 
                national or resident of any country to which the United States has embargoed goods or services, and 
                that you are not listed on any U.S. government list of prohibited or restricted parties.
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">15</span>
                Severability
              </h2>
              <p>
                If any provision of these Terms is found to be invalid, illegal, or unenforceable by a court of 
                competent jurisdiction, such provision shall be modified to the minimum extent necessary to make it 
                valid and enforceable while preserving its intent, or if such modification is not possible, such 
                provision shall be severed from these Terms. The invalidity or unenforceability of any provision 
                shall not affect the validity or enforceability of any other provision of these Terms, which shall 
                remain in full force and effect.
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">16</span>
                Waiver
              </h2>
              <p>
                No waiver of any term or condition of these Terms shall be deemed a further or continuing waiver of 
                such term or condition or any other term or condition. {brandName}&apos;s failure to assert any right or 
                provision under these Terms shall not constitute a waiver of such right or provision.
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">17</span>
                Assignment
              </h2>
              <p>
                You may not assign, transfer, or delegate these Terms or your rights and obligations hereunder 
                without {brandName}&apos;s prior written consent. {brandName} may assign these Terms or any rights hereunder 
                without restriction, including to any affiliate or in connection with a merger, acquisition, 
                corporate reorganization, or sale of all or substantially all of its assets. Any attempted assignment 
                in violation of this section shall be null and void. These Terms shall bind and inure to the benefit 
                of the parties and their permitted successors and assigns.
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">18</span>
                Survival
              </h2>
              <p>
                The following sections shall survive termination of these Terms: Data Privacy and Usage (Section 3), 
                Intellectual Property (Section 6), Limitation of Liability (Section 7), Governing Law and Dispute 
                Resolution (Section 10), Indemnification (Section 12), and any other provisions that by their nature 
                should survive termination.
              </p>
            </section>

            <section className="bg-vineyard-white rounded p-4 border-l-4 border-grape-600 shadow">
              <h2 className="text-base sm:text-lg font-semibold text-wood-900 mb-3 flex items-center">
                <span className="w-6 h-6 rounded-full bg-grape-100 text-grape-800 flex items-center justify-center text-xs font-bold mr-2">19</span>
                Contact Information
              </h2>
              <p>
                {brandName} is located in Dallas, Texas. If you have any questions about these 
                Terms, please contact us at:
              </p>
              <ul className="list-none pl-5 space-y-1.5 mt-2 text-wood-800">
                <li><strong className="text-wood-900">Email:</strong> WorkRefined@gmail.com</li>
                <li><strong className="text-wood-900">Location:</strong> Dallas, Texas</li>
              </ul>
              <p className="mt-3">
                For general customer support inquiries, please use the contact features within the Service.
              </p>
            </section>

            <div className="mt-8 pt-6 border-t-2 border-wood-300 bg-grape-50 rounded p-4">
              <p className="text-xs text-wood-800 leading-relaxed">
                <strong className="text-wood-900">By using {brandName}, you acknowledge that you have read, understood, and agree to be bound 
                by these Terms and Conditions.</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 sm:p-5 border-t-2 border-wood-300 bg-gradient-to-r from-vineyard-white via-vineyard-light to-vineyard-white sticky bottom-0 shadow-lg">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gradient-to-r from-grape-600 to-grape-700 hover:from-grape-800 hover:to-grape-900 text-vineyard-white rounded font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;

